import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Briefcase } from "lucide-react";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Parallax from "../components/Parallax";

function getOptimizedImage(url, width = 1000) {
  if (!url || !url.includes("assets.auxodev.dk")) return url;
  if (url.includes("/cdn-cgi/image/")) return url;
  return url.replace(
    "https://assets.auxodev.dk/",
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
}

export default function Projects() {
  const { lang, t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/v2/content/projects");
        if (res.ok) {
          const data = await res.json();
          setProjects(data.items || []);
          setFilteredProjects(data.items || []);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === "All") {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter((project) => {
        const roleDa = (project.role?.da || typeof project.role === "string" ? project.role : "").toLowerCase();
        const roleEn = (project.role?.en || typeof project.role === "string" ? project.role : "").toLowerCase();
        const typeDa = (project.project_type?.da || typeof project.project_type === "string" ? project.project_type : "").toLowerCase();
        const typeEn = (project.project_type?.en || typeof project.project_type === "string" ? project.project_type : "").toLowerCase();
        
        const searchFilter = filter.toLowerCase();
        
        // Map filter to English equivalent for matching if needed
        const filterMap = {
          "totalentreprise": "turnkey",
          "tømrerarbejde": "carpentry",
          "specialprojekter": "custom"
        };
        const mappedFilter = filterMap[searchFilter] || searchFilter;

        return (
          roleDa.includes(searchFilter) ||
          roleEn.includes(searchFilter) ||
          roleEn.includes(mappedFilter) ||
          typeDa.includes(searchFilter) ||
          typeEn.includes(searchFilter) ||
          typeEn.includes(mappedFilter)
        );
      });
      setFilteredProjects(filtered);
    }
  };

  const filters = ["All", "Turnkey", "Carpentry", "Custom Projects"];

  const filterLabels = {
    da: {
      "All": "Alle",
      "Turnkey": "Totalentreprise",
      "Carpentry": "Tømrerarbejde",
      "Custom Projects": "Specialprojekter"
    },
    en: {
      "All": "All",
      "Turnkey": "Turnkey",
      "Carpentry": "Carpentry",
      "Custom Projects": "Custom Projects"
    }
  };

  const homePath = lang === "da" ? "/" : "/en";
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const servicesPath = lang === "da" ? "/ydelser" : "/en/services";
  const aboutPath = lang === "da" ? "/om-os" : "/en/about";

  // Custom SEO titles and descriptions for Projects page
  const seoTitle = lang === "da" 
    ? "Referencer & Cases | CPH Collective" 
    : "References & Case Studies | CPH Collective";
  const seoDesc = lang === "da"
    ? "Se vores udvalgte byggeprojekter og tømrerarbejde udført for erhvervskunder og offentlige rum i København."
    : "Browse our portfolio of completed turnkey and carpentry projects for commercial clients and public spaces.";

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen pt-24 md:pt-32">
      {/* SEO Tags */}
      <SEO title={seoTitle} description={seoDesc} />

      {/* Header Section */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="max-w-4xl">
            <Parallax speed={0.04} type="text">
              <FadeIn direction="up">
                <span className="text-xs uppercase tracking-[0.4em] text-stone-500 font-mono block mb-4">
                  {t("projects.header.tag")}
                </span>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-stone-900 uppercase mb-8">
                  {t("projects.header.title")}
                </h1>
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
                  {lang === "da" ? (
                    <>
                      <Link
                        to={homePath}
                        className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                      >
                        CPH Collective
                      </Link>{" "}
                      har en dokumenteret erfaring med at levere stabile, veludførte løsninger til erhvervsbyggeri, offentlige rum og aktive byggepladser.
                    </>
                  ) : (
                    <>
                      <Link
                        to={homePath}
                        className="text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors"
                      >
                        CPH Collective
                      </Link>{" "}
                      has a proven track record of delivering stable, well-executed solutions for commercial buildings, public spaces, and active construction sites.
                    </>
                  )}
                </p>
              </FadeIn>
            </Parallax>
          </div>
        </div>
      </section>

      {/* Filter Controls */}
      <section className="py-8 border-b border-stone-200 bg-stone-50/80 sticky top-[72px] z-30 backdrop-blur-md">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <FadeIn direction="down" duration={500}>
            <div className="flex flex-wrap items-center gap-4 md:gap-8">
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-mono">
                {t("projects.filter.label")}
              </span>
              <div className="flex flex-wrap gap-2 md:gap-4">
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => handleFilterChange(filter)}
                    className={`text-xs uppercase tracking-[0.2em] font-mono px-4 py-2 border transition-all duration-300 cursor-pointer ${
                      activeFilter === filter
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-transparent text-stone-600 border-stone-200 hover:text-stone-900 hover:border-stone-400"
                    }`}
                  >
                    {filterLabels[lang]?.[filter] || filter}
                  </button>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-24">
        <div className="w-full px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="animate-pulse bg-stone-100 h-[450px] border border-stone-200" />
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-24 border border-stone-200">
              <p className="text-stone-500 font-mono text-sm">{t("projects.empty")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
              {filteredProjects.map((project, idx) => {
                const pTitle = typeof project.title === "object" ? project.title[lang] : project.title;
                const pType = typeof project.project_type === "object" ? project.project_type[lang] : project.project_type;
                const pLoc = typeof project.location === "object" ? project.location[lang] : project.location;
                const pRole = typeof project.role === "object" ? project.role[lang] : project.role;
                const pSlug = typeof project.project_slug === "object" ? project.project_slug[lang] : project.project_slug;
                const pDesc = typeof project.description === "object" ? project.description[lang] : project.description;

                return (
                  <FadeIn key={project.id} direction="up" delay={(idx % 2) * 150}>
                    <Link
                      to={lang === "da" ? `/projekter/${pSlug}` : `/en/projects/${pSlug}`}
                      className="group block border border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm transition-all duration-500"
                    >
                      {/* Image Container */}
                      <div className="relative h-[350px] md:h-[450px] overflow-hidden">
                        <Parallax speed={-0.05} type="background" className="w-full h-full">
                          <img
                            src={getOptimizedImage(project.images?.[0], 1000)}
                            alt={`${pTitle} – ${pType} | CPH Collective`}
                            className="w-full h-full object-cover object-center"
                          />
                        </Parallax>
                        <div className="absolute inset-0 bg-stone-950/10 group-hover:bg-transparent transition-colors duration-500 z-10 pointer-events-none" />
                      </div>

                      {/* Info Block */}
                      <div className="p-8">
                        <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500 font-mono mb-4">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {pLoc}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3.5 h-3.5" />
                            {pRole}
                          </span>
                        </div>

                        <h3 className="text-2xl font-light tracking-wide text-stone-900 uppercase mb-4 group-hover:text-stone-700 transition-colors">
                          {pTitle}
                        </h3>

                        <p className="text-stone-600 text-sm font-light leading-relaxed mb-6 line-clamp-2">
                          {pDesc.replace(/<[^>]*>/g, "")}
                        </p>

                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-900 font-mono border-b border-stone-200 pb-1 group-hover:border-stone-900 transition-colors">
                          {t("projects.viewDetails")}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    </Link>
                  </FadeIn>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Cross-linking Section to other subpages */}
      <section className="py-20 bg-stone-100/70 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Ydelser & Faglighed" : "Services & Disciplines"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Udforsk Vores Ydelser" : "Explore Our Services"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Læs om vores specialisering inden for totalentreprise, byggeprojektering, professionelt tømrerarbejde og byggepladsservice."
                    : "Read about our specialization in turnkey contracts, engineering, carpentry, and site services."}
                </p>
              </div>
              <Link
                to={servicesPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Se alle ydelser" : "View all services"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="p-8 bg-white border border-stone-200 flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-stone-400 block mb-3">
                  {lang === "da" ? "Virksomheden" : "The Company"}
                </span>
                <h3 className="text-xl font-light uppercase tracking-tight text-stone-900 mb-3">
                  {lang === "da" ? "Om CPH Collective" : "About CPH Collective"}
                </h3>
                <p className="text-stone-600 text-sm font-light leading-relaxed mb-6">
                  {lang === "da"
                    ? "Lær mere om vores håndværkstradition, værdier om ansvarlighed og gennemsigtighed samt vores faste byggeledelse."
                    : "Learn more about our craftsmanship tradition, values of transparency, and dedicated site management."}
                </p>
              </div>
              <Link
                to={aboutPath}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-stone-900 hover:text-stone-600 transition-colors border-b border-stone-900 pb-1 self-start"
              >
                <span>{lang === "da" ? "Læs om virksomheden" : "Learn about company"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-100 py-24 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16 text-center">
          <div className="max-w-3xl mx-auto">
            <FadeIn direction="up">
              <h2 className="text-3xl md:text-4xl font-light uppercase tracking-tight text-stone-900 mb-6">
                {t("projects.cta.title")}
              </h2>
              <p className="text-stone-600 text-base md:text-lg font-light mb-10 leading-relaxed">
                {t("projects.cta.text")}
              </p>
              <Link
                to={contactPath}
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white bg-stone-900 hover:bg-stone-800 transition-all duration-500 px-8 py-4 font-semibold font-mono"
              >
                {t("cta.contactTeam")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
